import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

export interface TraccarDevice {
  id: number;
  name: string;
  uniqueId: string;
  status: string;
  disabled: boolean;
  lastUpdate?: string;
  positionId?: number;
  groupId?: number;
  phone?: string;
  model?: string;
  contact?: string;
  category?: string;
  attributes?: Record<string, any>;
}

export interface TraccarPosition {
  id: number;
  deviceId: number;
  type?: string;
  protocol?: string;
  serverTime: string;
  deviceTime: string;
  fixTime: string;
  outdated: boolean;
  valid: boolean;
  latitude: number;
  longitude: number;
  altitude: number;
  speed: number;
  course: number;
  address?: string;
  accuracy?: number;
  network?: any;
  attributes: Record<string, any>;
}

export class TraccarService {
  private request: AxiosInstance;
  private token: string;

  constructor(baseURL: string, token: string) {
    this.token = token;
    // console.log('Creating TraccarService with:', { baseURL, hasToken: !!token });
    
    this.request = axios.create({
      baseURL,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    // Add request interceptor for debugging
    this.request.interceptors.request.use(
      (config) => {
        // console.log('Making request to:', config.url);
        // console.log('Headers:', config.headers);
        return config;
      },
      (error) => {
        console.error('Request setup error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for debugging
    this.request.interceptors.response.use(
      (response) => {
        // console.log('Response received:', response.status, response.data);
        return response;
      },
      (error) => {
        // console.error('Response error:', error.response?.status, error.response?.data);
        return Promise.reject(error);
      }
    );
  }

  async getDevices(config: AxiosRequestConfig = {}): Promise<TraccarDevice[]> {
    const response = await this.request.get<TraccarDevice[]>('/api/devices', config);
    return response.data;
  }

  async getDeviceByUniqueId(uniqueId: string, config: AxiosRequestConfig = {}): Promise<TraccarDevice[]> {
    const response = await this.request.get<TraccarDevice[]>('/api/devices', {
      ...config,
      params: { uniqueId, ...config.params }
    });
    return response.data;
  }

  async getDevice(deviceId: number, config: AxiosRequestConfig = {}): Promise<TraccarDevice> {
    const response = await this.request.get<TraccarDevice>(`/api/devices/${deviceId}`, config);
    return response.data;
  }

  async getPositions(deviceId?: number, from?: string, to?: string, config: AxiosRequestConfig = {}): Promise<TraccarPosition[]> {
    const params: any = {};
    if (deviceId) params.deviceId = deviceId;
    if (from) params.from = from;
    if (to) params.to = to;
    
    const response = await this.request.get<TraccarPosition[]>('/api/positions', {
      ...config,
      params: { ...params, ...config.params }
    });
    return response.data;
  }

  async getPositionsByDeviceAndPosition(deviceId: number, positionId: number, config: AxiosRequestConfig = {}): Promise<TraccarPosition[]> {
    const response = await this.request.get<TraccarPosition[]>('/api/positions', {
      ...config,
      params: { deviceId, id: positionId, ...config.params }
    });
    return response.data;
  }

  async getLatestPositionByUniqueId(uniqueId: string, config: AxiosRequestConfig = {}): Promise<TraccarPosition | null> {
    try {
      // First, get the device by uniqueId (like in your working example)
      const devices = await this.getDeviceByUniqueId(uniqueId, config);
      
      if (devices.length === 0) {
        console.log('No device found with uniqueId:', uniqueId);
        return null;
      }

      const device = devices[0];
      console.log('Found device:', device);

      if (!device.positionId) {
        console.log('Device has no positionId');
        return null;
      }

      // Then get the position using deviceId and positionId (like in your working example)
      const positions = await this.getPositionsByDeviceAndPosition(device.id, device.positionId, config);
      
      if (positions.length === 0) {
        console.log('No positions found for device');
        return null;
      }

      return positions[0];
    } catch (error) {
      console.error('Error in getLatestPositionByUniqueId:', error);
      throw error;
    }
  }

  async getLatestPosition(deviceId: number, config: AxiosRequestConfig = {}): Promise<TraccarPosition | null> {
    // Get positions for the last 24 hours to ensure we get recent data
    const to = new Date().toISOString();
    const from = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(); // 24 hours ago
    
    const positions = await this.getPositions(deviceId, from, to, config);
    if (positions.length === 0) {
      return null;
    }
    
    // Sort by fixTime to get the latest position
    const sortedPositions = positions.sort((a, b) => 
      new Date(b.fixTime).getTime() - new Date(a.fixTime).getTime()
    );
    
    return sortedPositions[0];
  }
}

// Create and export a default instance
const env = process.env;
const traccarBaseUrl = 'https://demo2.traccar.org';
// Try both possible environment variable names
const traccarToken = env.VUE_APP_PUBLIC_TRACCAR_TOKEN || env.VUE_APP_TRACCAR_TOKEN || '';

// console.log('Token from env:', traccarToken ? 'Token found' : 'No token found');
// console.log('Available env vars:', Object.keys(env).filter(key => key.includes('TRACCAR')));

export const traccarService = new TraccarService(traccarBaseUrl, traccarToken);
